/* ============================================================================
   MAISON de SABRÉ - Newsletter popup
   Patched 1 Sep 2026.

   WHAT CHANGED AND WHY
   1. Country list no longer comes from restcountries.com/v3.1 - that API is
      DEPRECATED (301s to a "this version has been deprecated" body). The fetch
      was failing silently inside an empty catch{}, so #countryDropdown rendered
      with ZERO items and the customer was locked on whatever the Liquid
      hardcoded. It now reads the theme's own assets/countries-data.json, whose
      URL is passed in from Liquid via data-countries-url.
   2. The default country is no longer hardcoded. It is taken from
      data-default-iso (Shopify's localization.country.iso_code) and resolved to
      the right flag + dial code once the list loads. The AU storefront serves
      three markets (Australia, NZ, ROW) so a flat +61 would also have been wrong.
   3. Phone numbers are normalised to E.164 before submit. Previously
      "0412 345 678" with +61 produced "+610412345678", which passed the local
      regex and was then rejected by Klaviyo. Spaces are stripped, a duplicated
      country code is stripped, and the national trunk "0" is dropped.
   4. Removed two redundant/competing dropdown click handlers that fought the
      real one.
   5. Failures are logged instead of swallowed, and the field stays usable.
   ========================================================================== */

(function () {
  'use strict';

  const form1 = document.querySelector('.newsletterStep1');
  const form2 = document.querySelector('.newsletterStep2');
  const form3 = document.querySelector('.newsletterStep3');

  // Fail safe: if the section is not on this page, do nothing rather than
  // throwing on the first querySelector and killing the whole script.
  if (!form1 || !form2 || !form3) return;

  const continueBtn1 = form1.querySelector('input[type="button"]');
  const continueBtn2 = form2.querySelector('input[type="button"]');
  const skipBtn = form2.querySelector('.newsletterSkipButton');
  const fieldsWrapper = form2.querySelector('.newsletterFieldsWrapper');

  const KLAVIYO_COMPANY_ID = 'MRf2w3';
  const KLAVIYO_LIST_ID = 'R5NEdt';
  const KLAVIYO_REVISION = '2024-10-15';

  // The JSON was generated from restcountries' idd.root + suffixes[0], which
  // concatenated an area code for every country that has more than one suffix.
  // These five are wrong in assets/countries-data.json. Correcting here means
  // the JSON does not have to be re-uploaded; fixing the JSON too is tidier.
  const DIAL_CODE_FIXES = {
    US: '+1',
    CA: '+1',
    VA: '+39',
    EH: '+212',
    AX: '+358',
  };

  // Surfaced first in the dropdown - the storefront's real markets.
  const PRIORITY_ISO = ['AU', 'NZ', 'US', 'GB', 'JP', 'SG', 'HK', 'CA'];

  const DEFAULT_ISO = 'AU';
  const DEFAULT_DIAL = '+61';

  form2.classList.add('hidden');
  form3.classList.add('hidden');

  function showForm(formToShow, ...formsToHide) {
    formToShow.classList.remove('hidden');
    formsToHide.forEach((form) => form.classList.add('hidden'));
  }

  /* ----------------------------------------------------------------------
     Phone normalisation
     ---------------------------------------------------------------------- */

  /**
   * Turn a dial code plus whatever the customer typed into an E.164 number.
   * Returns null if it cannot be made valid.
   *
   *   ('+61', '0412 345 678')   -> '+61412345678'
   *   ('+61', '61412345678')    -> '+61412345678'
   *   ('+61', '412345678')      -> '+61412345678'
   *   ('+1',  '(212) 555-1234') -> '+12125551234'
   */
  function toE164(dialCode, rawInput) {
    const dialDigits = String(dialCode || '').replace(/\D/g, '');
    let digits = String(rawInput || '').replace(/\D/g, '');

    if (!dialDigits || !digits) return null;

    // The customer pasted the full international number including the country
    // code. Only strip it when a plausible national number remains, so we do
    // not mangle a genuine national number that happens to start with the
    // same digits.
    if (digits.length > dialDigits.length + 5 && digits.indexOf(dialDigits) === 0) {
      const candidate = digits.slice(dialDigits.length);
      if (candidate.replace(/^0+/, '').length >= 6) digits = candidate;
    }

    // AU, NZ, GB and many others write mobiles with a leading trunk 0 that is
    // dropped in E.164. This is the bug that made every AU number fail.
    digits = digits.replace(/^0+/, '');

    const e164 = '+' + dialDigits + digits;
    return /^\+[1-9]\d{7,14}$/.test(e164) ? e164 : null;
  }

  function currentDialCode() {
    const el = document.querySelector('#countryButton .countryCode');
    return el ? el.textContent.trim() : DEFAULT_DIAL;
  }

  /* ----------------------------------------------------------------------
     Country picker
     ---------------------------------------------------------------------- */

  document.addEventListener('DOMContentLoaded', function () {
    const countryButton = document.getElementById('countryButton');
    const countryDropdown = document.getElementById('countryDropdown');
    if (!countryButton || !countryDropdown) return;

    const countriesUrl =
      (fieldsWrapper && fieldsWrapper.getAttribute('data-countries-url')) || '';
    const defaultIso = (
      (fieldsWrapper && fieldsWrapper.getAttribute('data-default-iso')) ||
      DEFAULT_ISO
    ).toUpperCase();

    // Populated by loadCountries(); used by the open/close handlers below.
    let searchInput = null;
    let items = [];

    function applySelection(flagUrl, dialCode) {
      const img = countryButton.querySelector('.flagIcon img');
      const code = countryButton.querySelector('.countryCode');
      if (img && flagUrl) img.src = flagUrl;
      if (code && dialCode) code.textContent = dialCode;
    }

    function resetFilter() {
      if (searchInput) searchInput.value = '';
      items.forEach(function (entry) {
        entry.el.style.display = '';
      });
    }

    function closeDropdown() {
      countryDropdown.style.display = 'none';
      countryDropdown.classList.remove('dropdownActive');
      countryButton.setAttribute('aria-expanded', 'false');
      resetFilter();
    }

    function buildItem(country) {
      const item = document.createElement('button');
      item.type = 'button';
      item.classList.add('dropdown-item');
      item.setAttribute('role', 'option');
      item.setAttribute('data-iso', country.code);
      item.title = country.name + ' (' + country.dialCode + ')';
      item.setAttribute('aria-label', item.title);
      item.innerHTML =
        '<img src="' + country.flagPng + '" alt="' + country.name + '" style="width: 20px;">' +
        '<span class="countryName">' + country.name + '</span>' +
        '<span class="countryCode">' + country.dialCode + '</span>';
      item.addEventListener('click', function () {
        applySelection(country.flagPng, country.dialCode);
        closeDropdown();
      });
      return item;
    }

    async function loadCountries() {
      if (!countriesUrl) {
        console.warn('[newsletter-popup] data-countries-url missing on .newsletterFieldsWrapper');
        return;
      }

      let data;
      try {
        const response = await fetch(countriesUrl);
        if (!response.ok) throw new Error('HTTP ' + response.status);
        data = await response.json();
      } catch (error) {
        // Do NOT swallow this. If it ever breaks again we want it visible.
        console.error('[newsletter-popup] country list failed to load:', error);
        return;
      }

      if (!Array.isArray(data) || !data.length) {
        console.error('[newsletter-popup] country list was empty or malformed');
        return;
      }

      const countries = data
        .filter((c) => c && c.code && c.dialCode && (c.flagPng || c.flagSvg))
        .map((c) => ({
          name: c.name,
          code: String(c.code).toUpperCase(),
          dialCode: DIAL_CODE_FIXES[String(c.code).toUpperCase()] || c.dialCode,
          flagPng: c.flagPng || c.flagSvg,
        }));

      countries.sort(function (a, b) {
        const ai = PRIORITY_ISO.indexOf(a.code);
        const bi = PRIORITY_ISO.indexOf(b.code);
        if (ai !== -1 || bi !== -1) {
          return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        }
        return a.name.localeCompare(b.name);
      });

      // 248 countries in a scrolling list is unusable without a filter.
      items = countries.map(function (c) {
        return {
          el: buildItem(c),
          haystack: (c.name + ' ' + c.code + ' ' + c.dialCode).toLowerCase(),
        };
      });

      searchInput = document.createElement('input');
      searchInput.type = 'search';
      searchInput.className = 'newsletterCountrySearch';
      searchInput.placeholder = 'Search country';
      searchInput.setAttribute('aria-label', 'Search country');
      // Stop the outside-click handler closing the dropdown on focus.
      searchInput.addEventListener('click', function (e) {
        e.stopPropagation();
      });
      searchInput.addEventListener('input', function () {
        const q = searchInput.value.trim().toLowerCase();
        items.forEach(function (entry) {
          entry.el.style.display = !q || entry.haystack.indexOf(q) !== -1 ? '' : 'none';
        });
      });

      const frag = document.createDocumentFragment();
      frag.appendChild(searchInput);
      items.forEach((entry) => frag.appendChild(entry.el));
      countryDropdown.innerHTML = '';
      countryDropdown.appendChild(frag);

      // Set the default from Shopify's localization context. The popup only
      // opens after 6s, so this has always resolved before it is seen.
      const match =
        countries.find((c) => c.code === defaultIso) ||
        countries.find((c) => c.code === DEFAULT_ISO);
      if (match) applySelection(match.flagPng, match.dialCode);
    }

    countryButton.addEventListener('click', function () {
      const isVisible = countryDropdown.style.display === 'block';
      if (isVisible) {
        closeDropdown();
        return;
      }
      countryDropdown.style.display = 'block';
      countryDropdown.classList.add('dropdownActive');
      countryButton.setAttribute('aria-expanded', 'true');
      if (searchInput) searchInput.focus();
    });

    document.addEventListener('click', function (event) {
      if (!countryButton.contains(event.target) && !countryDropdown.contains(event.target)) {
        closeDropdown();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeDropdown();
    });

    loadCountries();
  });

  /* ----------------------------------------------------------------------
     Step 1 - email
     ---------------------------------------------------------------------- */

  continueBtn1.addEventListener('click', () => {
    const emailField = document.getElementById('email');
    const emailValue = emailField.value.trim();
    const emailError = document.getElementById('error-message');
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailValue) {
      emailError.textContent = 'Please enter your email address.';
      emailError.style.display = 'block';
    } else if (!emailPattern.test(emailValue)) {
      emailError.textContent = 'Please enter a valid email address.';
      emailError.style.display = 'block';
    } else {
      emailError.style.display = 'none';
      showForm(form2, form1, form3);
    }
  });

  continueBtn2.addEventListener('click', () => {
    createSubscription();
  });

  skipBtn.addEventListener('click', () => {
    createSubscriptionwithemail();
  });

  /* ----------------------------------------------------------------------
     Modal open / close
     ---------------------------------------------------------------------- */

  document.addEventListener('DOMContentLoaded', function () {
    const body = document.body;
    const modelContainer = document.querySelector('.newsletterModalContainer');
    const modelOverlay = document.querySelector('.newsletterModalOverlay');
    const modelCloser = document.querySelector('.newsletterModalCloseButton');
    const modelCloser1 = document.querySelector('.newsletterModalClosemodel');
    if (!modelContainer || !modelOverlay) return;

    function closeModal() {
      modelOverlay.classList.remove('active');
      modelContainer.classList.remove('active');
      modelContainer.setAttribute('aria-hidden', 'true');
      modelContainer.style.opacity = '0';
      body.style.overflow = '';
    }

    modelOverlay.addEventListener('click', (e) => {
      if (e.target === modelOverlay) closeModal();
    });

    if (!localStorage.getItem('hasVisited')) {
      setTimeout(function () {
        modelOverlay.classList.add('active');
        modelContainer.classList.add('active');
        modelContainer.setAttribute('aria-hidden', 'false');
        modelContainer.style.opacity = '1';
        body.style.overflow = 'hidden';
        localStorage.setItem('hasVisited', 'true');
      }, 6000);
    }

    if (modelCloser) modelCloser.addEventListener('click', closeModal);
    if (modelCloser1) modelCloser1.addEventListener('click', closeModal);
  });

  /* ----------------------------------------------------------------------
     Klaviyo submit
     ---------------------------------------------------------------------- */

  function klaviyoLocale() {
    return (fieldsWrapper && fieldsWrapper.getAttribute('data-locale')) || 'en-AU';
  }

  function klaviyoSource() {
    return (
      (fieldsWrapper && fieldsWrapper.getAttribute('data-custom-source')) ||
      'Newsletter Popup'
    );
  }

  async function postSubscription(profileAttributes, errorEl) {
    const url =
      'https://a.klaviyo.com/client/subscriptions?company_id=' + KLAVIYO_COMPANY_ID;

    const body = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes,
            },
            meta: { patch_properties: { append: {} } },
          },
          custom_source: klaviyoSource(),
        },
        relationships: {
          list: { data: { type: 'list', id: KLAVIYO_LIST_ID } },
        },
      },
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json',
          Revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(body),
      });

      if (response.status === 202) {
        showForm(form3, form1, form2);
        return true;
      }

      if (response.status === 400) {
        let detail = 'Sorry, we could not complete your sign-up. Please check your details.';
        try {
          const errorData = await response.json();
          detail = errorData.errors[0].detail || detail;
        } catch (e) {
          /* keep the fallback message */
        }
        if (errorEl) {
          errorEl.textContent = detail;
          errorEl.style.display = 'block';
        }
        return false;
      }

      throw new Error('HTTP error! status: ' + response.status);
    } catch (error) {
      console.error('[newsletter-popup] subscription failed:', error);
      if (errorEl) {
        errorEl.textContent = 'Something went wrong. Please try again.';
        errorEl.style.display = 'block';
      }
      return false;
    }
  }

  async function createSubscription() {
    const mobileNumberInput = document.getElementById('mobileNumber');
    const mobileError = document.getElementById('mobileError');
    const emailInput = document.getElementById('email');
    const currentUrl = window.location.href;

    const raw = mobileNumberInput.value.trim();

    if (!raw) {
      mobileError.textContent = 'Please enter your phone number.';
      mobileError.style.display = 'block';
      return;
    }

    const phone = toE164(currentDialCode(), raw);

    if (!phone) {
      mobileError.textContent =
        'Please enter a valid mobile number for the country code shown.';
      mobileError.style.display = 'block';
      return;
    }

    mobileError.style.display = 'none';

    await postSubscription(
      {
        email: emailInput.value.trim(),
        phone_number: phone,
        locale: klaviyoLocale(),
        properties: {
          $referrer: { first_page: currentUrl },
          $last_referrer: { first_page: currentUrl },
          $source: '(EN) Custom Newsletter Pop-Up',
        },
        // To record explicit SMS marketing consent, add the block below.
        // Left out deliberately: switching it on is a compliance decision
        // (disclosure wording, terms and privacy links) not a code one.
        //
        // subscriptions: {
        //   email: { marketing: { consent: 'SUBSCRIBED' } },
        //   sms: { marketing: { consent: 'SUBSCRIBED' } },
        // },
      },
      mobileError
    );
  }

  async function createSubscriptionwithemail() {
    const emailInput = document.getElementById('email');
    const mobileError = document.getElementById('mobileError');
    const currentUrl = window.location.href;
    const email = emailInput.value.trim();

    await postSubscription(
      {
        email: email,
        locale: klaviyoLocale(),
        properties: {
          $referrer: { first_page: currentUrl },
          $last_referrer: { first_page: currentUrl },
          $source: '(EN) Custom Newsletter Pop-Up',
        },
      },
      mobileError
    );
  }
})();
